// src/components/study/CrcNamesModal.js
//
// "CRC names" pop-up (Admin Dashboard, admins; UI revamp). Links the full names
// written on studies (Lead CRC, "JALAK UPADHYAY") to coordinators on Manage
// Coordinators ("Jalak"), so RegX knows who they are. Studies keep their full
// names and Manage Coordinators is not changed. Rules: services/crcNames.js;
// storage: crcNamesService.js (crc_names; admins write, firestore.rules).
//
//   - Linked: each full name and its coordinator; Unlink (asks first).
//   - On studies, not linked yet: every other full name used on a study, with
//     a suggested coordinator (their first name, only if exactly one fits);
//     the admin confirms each with Link. Nothing links by itself.
//   - Add a CRC name: a full name for a coordinator (e.g. a new hire), or tick
//     "Use the same name as on Manage Coordinators".

import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link2, Unlink, X } from "lucide-react";
import { auth } from "../../firebase";
import SearchableSelect from "../SearchableSelect";
import { fetchCoordinators } from "../../services/coordinatorService";
import { loadCrcNames, removeCrcName, saveCrcName } from "../../services/crcNamesService";
import { unlinkedStudyNames, validateLink } from "../../services/crcNames";

const UnlinkedRow = ({ row, coordinatorNames, onLink }) => {
  const [pick, setPick] = useState(row.suggestion || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const link = async () => {
    setBusy(true);
    setError("");
    const err = await onLink({ fullName: row.fullName, coordinator: pick, sameAsCoordinator: false });
    if (err) {
      setError(err);
      setBusy(false);
    }
  };
  return (
    <li className="rx-crc-row" aria-label={row.fullName}>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium break-words">{row.fullName}</div>
        <div className="rx-home-muted text-[11px]">
          On {row.studies} {row.studies === 1 ? "study" : "studies"}
          {row.suggestion ? ` · suggested: ${row.suggestion}` : " · no sure match"}
        </div>
        {error && <div className="rx-home-warn text-[11px] mt-0.5">{error}</div>}
      </div>
      <div className="w-44 shrink-0">
        <SearchableSelect
          value={pick}
          options={coordinatorNames}
          onChange={setPick}
          placeholder="Pick a coordinator"
          disabled={busy}
          compact
        />
      </div>
      <button type="button" className="rx-home-button shrink-0" onClick={link} disabled={!pick || busy}>
        <Link2 className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
        Link
      </button>
    </li>
  );
};

export default function CrcNamesModal({ studies, onClose }) {
  const [state, setState] = useState({ loading: true, error: "", roster: [], links: [] });
  const [confirmUnlink, setConfirmUnlink] = useState(null);
  const [form, setForm] = useState({ coordinator: "", fullName: "", same: false });
  const [formError, setFormError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    try {
      const [roster, links] = await Promise.all([fetchCoordinators(), loadCrcNames()]);
      setState({ loading: false, error: "", roster, links });
    } catch (e) {
      console.error("Couldn't load the CRC names:", e);
      setState({ loading: false, error: "Couldn't load the CRC names right now.", roster: [], links: [] });
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const activeNames = useMemo(
    () => state.roster.filter((c) => c && c.name && c.active !== false).map((c) => c.name).sort((a, b) => a.localeCompare(b)),
    [state.roster]
  );
  const linked = useMemo(
    () => [...state.links].sort((a, b) => String(a.fullName).localeCompare(String(b.fullName))),
    [state.links]
  );
  const unlinked = useMemo(() => unlinkedStudyNames(studies, state.links, state.roster), [studies, state]);

  // Save a link; returns an error message, or "" when saved.
  const saveLink = async (input) => {
    const r = validateLink(input, state.roster, state.links);
    if (!r.ok) return r.error;
    try {
      await saveCrcName(r.link, auth.currentUser);
      setState((s) => ({ ...s, links: [...s.links.filter((l) => l.id !== r.link.id), r.link] }));
      setNotice(`Linked "${r.link.fullName}" to ${r.link.coordinator}.`);
      return "";
    } catch (e) {
      console.error("Couldn't save the CRC name:", e);
      return "Couldn't save. Please try again.";
    }
  };

  const addFromForm = async (e) => {
    e.preventDefault();
    setFormError("");
    const err = await saveLink({ fullName: form.fullName, coordinator: form.coordinator, sameAsCoordinator: form.same });
    if (err) setFormError(err);
    else setForm({ coordinator: "", fullName: "", same: false });
  };

  const unlink = async () => {
    const l = confirmUnlink;
    setConfirmUnlink(null);
    try {
      await removeCrcName(l.id);
      setState((s) => ({ ...s, links: s.links.filter((x) => x.id !== l.id) }));
      setNotice(`Unlinked "${l.fullName}". Studies keep the name; it just isn't linked to ${l.coordinator} any more.`);
    } catch (e) {
      console.error("Couldn't unlink:", e);
      setNotice("Couldn't unlink. Please try again.");
    }
  };

  return createPortal(
    // Closes only on a click on the dark backdrop itself. Clicks inside are NOT
    // stopped, so the coordinator pickers hear them and close their lists when
    // you click away (SearchableSelect listens on the document).
    <div
      className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="crc-names-title"
        className="rx-crc-modal w-full max-w-2xl max-h-[88vh] overflow-y-auto"
      >
        <div className="flex items-start gap-3 mb-2">
          <div className="flex-1">
            <h2 id="crc-names-title" className="rx-home-title">
              CRC names
            </h2>
            <p className="rx-home-muted text-xs mt-1">
              Link the full names used on studies to coordinators on Manage Coordinators, so RegX knows who they are.
              Studies keep their full names, and Manage Coordinators isn't changed.
            </p>
          </div>
          <button type="button" className="rx-icon-button" aria-label="Close" onClick={onClose}>
            <X className="w-4 h-4" />
          </button>
        </div>
        {notice && (
          <p className="text-xs mb-2" role="status">
            {notice}
          </p>
        )}

        {state.loading ? (
          <p className="rx-home-muted text-sm">Loading…</p>
        ) : state.error ? (
          <p className="rx-home-warn text-sm">{state.error}</p>
        ) : (
          <div className="space-y-5">
            <section aria-label="Linked">
              <h3 className="text-sm font-semibold mb-1.5">Linked ({linked.length})</h3>
              {linked.length === 0 ? (
                <p className="rx-home-muted text-xs">No names linked yet.</p>
              ) : (
                <ul className="space-y-1">
                  {linked.map((l) => (
                    <li key={l.id} className="rx-crc-row" aria-label={l.fullName}>
                      <div className="min-w-0 flex-1 text-sm">
                        <span className="font-medium break-words">{l.fullName}</span>
                        <span className="rx-home-muted"> → {l.coordinator}</span>
                        {l.sameAsCoordinator && <span className="rx-crc-tag">Same as Manage Coordinators</span>}
                      </div>
                      {confirmUnlink && confirmUnlink.id === l.id ? (
                        <span className="flex items-center gap-2 shrink-0 text-xs">
                          Unlink?
                          <button type="button" className="rx-home-link" onClick={unlink}>
                            Yes
                          </button>
                          <button type="button" className="rx-home-link" onClick={() => setConfirmUnlink(null)}>
                            No
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="rx-home-link shrink-0 inline-flex items-center gap-1"
                          onClick={() => setConfirmUnlink(l)}
                          aria-label={`Unlink ${l.fullName}`}
                        >
                          <Unlink className="w-3.5 h-3.5" aria-hidden="true" />
                          Unlink
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section aria-label="On studies, not linked yet">
              <h3 className="text-sm font-semibold mb-1">On studies, not linked yet ({unlinked.length})</h3>
              <p className="rx-home-muted text-[11px] mb-1.5">
                Check the coordinator for each and click Link. A suggestion is shown only when exactly one coordinator's
                name is its first name. Names of people who have left can stay unlinked.
              </p>
              {unlinked.length === 0 ? (
                <p className="rx-home-muted text-xs">Every name on a study is linked.</p>
              ) : (
                <ul className="space-y-1">
                  {unlinked.map((u) => (
                    <UnlinkedRow key={u.fullName} row={u} coordinatorNames={activeNames} onLink={saveLink} />
                  ))}
                </ul>
              )}
            </section>

            <section aria-label="Add a CRC name">
              <h3 className="text-sm font-semibold mb-1.5">Add a CRC name</h3>
              <form onSubmit={addFromForm} className="space-y-2">
                <div className="w-64 max-w-full">
                  <SearchableSelect
                    value={form.coordinator}
                    options={activeNames}
                    onChange={(name) => setForm((f) => ({ ...f, coordinator: name }))}
                    placeholder="Pick a coordinator"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="rx-home-check"
                    checked={form.same}
                    onChange={(e) => setForm((f) => ({ ...f, same: e.target.checked }))}
                  />
                  Use the same name as on Manage Coordinators
                </label>
                <div>
                  <label htmlFor="crc-full-name" className="rx-home-muted text-xs block mb-1">
                    Full name, as it should show on studies
                  </label>
                  <input
                    id="crc-full-name"
                    value={form.same ? form.coordinator : form.fullName}
                    onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
                    disabled={form.same}
                    maxLength={80}
                    placeholder="e.g. Jalak Upadhyay"
                    className="rx-home-input w-full max-w-sm"
                  />
                </div>
                {formError && <p className="rx-home-warn text-xs">{formError}</p>}
                <button type="submit" className="rx-home-button">
                  Add
                </button>
              </form>
            </section>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
